import { useMemo, useState } from 'react'
import { ChevronDown, ChevronRight, Plus, Search, Sparkles, X } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Combobox } from '@/components/Combobox'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/cn'
import { formatCurrencyPrecise, formatDate } from '@/lib/locale'
import type { TaxonomyItem } from '@/features/coa/types'
import type { ClassifiedRow, CoaSuggestion } from '@/features/upload/types'

export interface RowKey {
  tIdx: number
  rIdx: number
}

export interface ClassificationGroup {
  key: string
  main: ClassifiedRow & RowKey
  children: Array<ClassifiedRow & RowKey>
  suggestions: CoaSuggestion[]
}

export interface CoaSelection {
  statement_type: string
  main_category: string
  subcategory: string
  account_code: string
}

interface NewRecordDraft {
  statement_type: string
  main_category: string
  subcategory: string
  account_code: string
}

const EMPTY_DRAFT: NewRecordDraft = {
  statement_type: '',
  main_category: '',
  subcategory: '',
  account_code: '',
}

interface Props {
  group: ClassificationGroup
  selection: CoaSelection | null
  taxonomy: TaxonomyItem[]
  onSelect: (sel: CoaSelection) => void
  onClear: () => void
  onCreateRecord: (draft: NewRecordDraft) => Promise<void>
}

function suggestionKey(s: CoaSuggestion): string {
  return [s.statement_type, s.main_category, s.subcategory, s.account_code ?? ''].join('|')
}

function selectionFromSuggestion(s: CoaSuggestion): CoaSelection {
  return {
    statement_type: s.statement_type,
    main_category: s.main_category,
    subcategory: s.subcategory,
    account_code: s.account_code ?? '',
  }
}

function selectionsMatch(a: CoaSelection | null, s: CoaSuggestion): boolean {
  if (!a) return false
  return (
    a.statement_type === s.statement_type &&
    a.main_category === s.main_category &&
    a.subcategory === s.subcategory &&
    (a.account_code ?? '') === (s.account_code ?? '')
  )
}

function CoaPath({ sel }: { sel: CoaSelection }) {
  return (
    <div className="flex flex-wrap items-center gap-1 text-xs">
      <Badge variant="secondary">{sel.statement_type}</Badge>
      <ChevronRight className="size-3 opacity-60" />
      <Badge variant="secondary">{sel.main_category}</Badge>
      <ChevronRight className="size-3 opacity-60" />
      <Badge variant="secondary">{sel.subcategory}</Badge>
      {sel.account_code ? (
        <span className="ml-1 rounded-sm border border-foreground/20 bg-background/90 px-1.5 py-0.5 font-mono text-[11px] font-semibold text-foreground">
          {sel.account_code}
        </span>
      ) : null}
    </div>
  )
}

export function ClassificationGroupCard({
  group,
  selection,
  taxonomy,
  onSelect,
  onClear,
  onCreateRecord,
}: Props) {
  const [showSimilar, setShowSimilar] = useState(false)
  const [pickerMode, setPickerMode] = useState<'code' | 'category'>('code')
  const [showNewRecord, setShowNewRecord] = useState(false)
  const [draft, setDraft] = useState<NewRecordDraft>(EMPTY_DRAFT)
  const [draftError, setDraftError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)

  const codeOptions = useMemo(
    () =>
      taxonomy
        .filter((t) => !!t.account_code)
        .map((t) => ({
          value: t.account_code,
          label: t.account_code,
          description: `${t.statement_type} › ${t.main_category} › ${t.subcategory_name}`,
        })),
    [taxonomy],
  )

  const subcategoryOptions = useMemo(
    () =>
      taxonomy.map((t) => ({
        value: String(t.id),
        label: t.subcategory_name,
        description: `${t.statement_type} › ${t.main_category}${t.account_code ? ` · ${t.account_code}` : ''}`,
      })),
    [taxonomy],
  )

  const taxonomyById = useMemo(() => {
    const m = new Map<string, TaxonomyItem>()
    for (const t of taxonomy) m.set(String(t.id), t)
    return m
  }, [taxonomy])

  const taxonomyByCode = useMemo(() => {
    const m = new Map<string, TaxonomyItem>()
    for (const t of taxonomy) {
      if (t.account_code) m.set(t.account_code, t)
    }
    return m
  }, [taxonomy])

  const main = group.main
  const childCount = group.children.length

  // Which panel is "owning" the current selection? Used to drive the
  // focused/dimmed visual treatment - AI panel is highlighted by default
  // when a matching suggestion is selected; manual picker takes over the
  // highlight as soon as the user picks something outside the AI list.
  const aiMatches = group.suggestions.some((s) => selectionsMatch(selection, s))
  const manualActive = !!selection && !aiMatches
  const aiFocused = !manualActive

  const handleSubmitNew = async () => {
    setDraftError(null)
    if (!draft.statement_type || !draft.main_category.trim() || !draft.subcategory.trim() || !draft.account_code.trim()) {
      setDraftError('Please fill in all four fields.')
      return
    }
    if (taxonomyByCode.has(draft.account_code.trim())) {
      const existing = taxonomyByCode.get(draft.account_code.trim())!
      setDraftError(
        `Account code "${draft.account_code}" already exists under ${existing.statement_type} › ${existing.main_category} › ${existing.subcategory_name}.`,
      )
      return
    }
    setCreating(true)
    try {
      await onCreateRecord({
        statement_type: draft.statement_type,
        main_category: draft.main_category.trim(),
        subcategory: draft.subcategory.trim(),
        account_code: draft.account_code.trim(),
      })
      onSelect({
        statement_type: draft.statement_type,
        main_category: draft.main_category.trim(),
        subcategory: draft.subcategory.trim(),
        account_code: draft.account_code.trim(),
      })
      setDraft(EMPTY_DRAFT)
      setShowNewRecord(false)
    } catch (err) {
      setDraftError(err instanceof Error ? err.message : 'Failed to save new record.')
    } finally {
      setCreating(false)
    }
  }

  const onPickFromCode = (code: string) => {
    const t = taxonomyByCode.get(code)
    if (!t) return
    onSelect({
      statement_type: t.statement_type,
      main_category: t.main_category,
      subcategory: t.subcategory_name,
      account_code: t.account_code,
    })
  }

  const onPickFromSubcategory = (id: string) => {
    const t = taxonomyById.get(id)
    if (!t) return
    onSelect({
      statement_type: t.statement_type,
      main_category: t.main_category,
      subcategory: t.subcategory_name,
      account_code: t.account_code,
    })
  }

  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        {/* Main transaction header */}
        <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-border pb-2">
          <div className="min-w-0 flex-1">
            <div className="text-xs text-muted-foreground">
              {main.date ? formatDate(main.date) : ''}
            </div>
            <div className="break-words font-semibold text-foreground">
              {main.description ?? '(no description)'}
            </div>
          </div>
          <div className="font-mono text-sm font-semibold">
            {formatCurrencyPrecise(parseFloat(String(main.amount ?? 0)) || 0)}
          </div>
        </div>

        {/* AI suggestions */}
        <div
          className={cn(
            'space-y-2 rounded-md border p-3 transition-colors',
            aiFocused && group.suggestions.length > 0
              ? 'border-primary/40 bg-primary/5 ring-1 ring-primary/30'
              : 'border-border bg-card opacity-80',
          )}
        >
          <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            <Sparkles className="size-3 text-primary" />
            Top AI Chart of Account matches
          </div>
          {group.suggestions.length === 0 ? (
            <div className="rounded-md border border-dashed border-border p-3 text-center text-xs text-muted-foreground">
              AI predictions unavailable - pick a COA below.
            </div>
          ) : (
            <div className="flex flex-col gap-1.5">
              {group.suggestions.slice(0, 3).map((s) => {
                const isSelected = selectionsMatch(selection, s)
                return (
                  <Button
                    key={suggestionKey(s)}
                    type="button"
                    variant={isSelected ? 'primary' : 'outline'}
                    className={cn(
                      'h-auto justify-start p-2 text-left text-xs font-normal',
                      isSelected && 'ring-2 ring-primary/40',
                    )}
                    onClick={() => onSelect(selectionFromSuggestion(s))}
                  >
                    <CoaPath sel={selectionFromSuggestion(s)} />
                  </Button>
                )
              })}
            </div>
          )}
        </div>

        {/* Manual COA picker */}
        <div
          className={cn(
            'space-y-2 rounded-md border p-3 transition-colors',
            manualActive
              ? 'border-primary/40 bg-primary/5 ring-1 ring-primary/30'
              : 'border-border bg-muted/30 opacity-90',
          )}
        >
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex gap-1">
              <Button
                type="button"
                size="sm"
                variant={pickerMode === 'code' ? 'primary' : 'ghost'}
                onClick={() => setPickerMode('code')}
              >
                <Search className="size-3" />
                By Code
              </Button>
              <Button
                type="button"
                size="sm"
                variant={pickerMode === 'category' ? 'primary' : 'ghost'}
                onClick={() => setPickerMode('category')}
              >
                <Search className="size-3" />
                By Subcategory
              </Button>
            </div>
            <div className="flex gap-1">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => {
                  setShowNewRecord((v) => !v)
                  setDraftError(null)
                }}
                title="Add new COA"
              >
                <Plus className="size-3" />
                {showNewRecord ? 'Cancel new' : 'Add new'}
              </Button>
              {selection ? (
                <Button type="button" size="sm" variant="ghost" onClick={onClear} title="Clear">
                  <X className="size-3" />
                </Button>
              ) : null}
            </div>
          </div>

          {pickerMode === 'code' ? (
            <Combobox
              value={selection?.account_code || null}
              onChange={(v) => v && onPickFromCode(String(v))}
              options={codeOptions}
              placeholder="Type account code…"
            />
          ) : (
            <Combobox
              value={null}
              onChange={(v) => v && onPickFromSubcategory(String(v))}
              options={subcategoryOptions}
              placeholder="Search subcategory…"
            />
          )}

          {selection ? (
            <div className="rounded-sm bg-card p-2">
              <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Selected
              </div>
              <CoaPath sel={selection} />
            </div>
          ) : null}
        </div>

        {/* Add-new-record inline form */}
        {showNewRecord ? (
          <div className="space-y-2 rounded-md border border-primary/30 bg-primary/5 p-3">
            <div className="flex items-center gap-1 text-sm font-semibold">
              <Plus className="size-4" />
              Create new taxonomy record
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              <div>
                <label className="text-xs text-muted-foreground">Statement Type</label>
                <Select
                  value={draft.statement_type || undefined}
                  onValueChange={(v) => setDraft({ ...draft, statement_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select…" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Income Statement">Income Statement</SelectItem>
                    <SelectItem value="Balance Sheet">Balance Sheet</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Main Category</label>
                <Input
                  value={draft.main_category}
                  onChange={(e) => setDraft({ ...draft, main_category: e.currentTarget.value })}
                  placeholder="e.g. Revenue"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Subcategory</label>
                <Input
                  value={draft.subcategory}
                  onChange={(e) => setDraft({ ...draft, subcategory: e.currentTarget.value })}
                  placeholder="e.g. Sales"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Account Code</label>
                <Input
                  value={draft.account_code}
                  onChange={(e) => setDraft({ ...draft, account_code: e.currentTarget.value })}
                  placeholder="e.g. 4001"
                />
              </div>
            </div>
            {draftError ? (
              <Alert variant="destructive">
                <AlertDescription className="text-xs">{draftError}</AlertDescription>
              </Alert>
            ) : null}
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={() => {
                  setShowNewRecord(false)
                  setDraftError(null)
                  setDraft(EMPTY_DRAFT)
                }}
                disabled={creating}
              >
                Cancel
              </Button>
              <Button type="button" size="sm" onClick={handleSubmitNew} disabled={creating}>
                {creating ? 'Saving…' : 'Use this record'}
              </Button>
            </div>
          </div>
        ) : null}

        {/* Similar transactions */}
        {childCount > 0 ? (
          <div>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setShowSimilar((v) => !v)}
              className="h-auto p-1 text-xs text-muted-foreground hover:text-foreground"
            >
              {showSimilar ? <ChevronDown className="size-3" /> : <ChevronRight className="size-3" />}
              {childCount} similar transaction{childCount === 1 ? '' : 's'}
              {showSimilar ? ' - click to hide' : ' - click to view'}
            </Button>
            {showSimilar ? (
              <div className="mt-1 space-y-1 rounded-md border border-border bg-muted/40 p-2">
                {group.children.map((c) => (
                  <div
                    key={`${c.tIdx}-${c.rIdx}`}
                    className="flex flex-wrap items-baseline justify-between gap-2 border-b border-border/60 py-1 text-xs last:border-0"
                  >
                    <div className="text-muted-foreground">
                      {c.date ? formatDate(c.date) : ''}
                    </div>
                    <div className="min-w-0 flex-1 break-words text-foreground">
                      {c.description ?? '(no description)'}
                    </div>
                    <div className="font-mono">
                      {formatCurrencyPrecise(parseFloat(String(c.amount ?? 0)) || 0)}
                    </div>
                  </div>
                ))}
                <div className="pt-1 text-[11px] text-muted-foreground">
                  All similar transactions inherit the COA you select above.
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
      </CardContent>
    </Card>
  )
}
